/**
 * Category icons are emoji (PO decision, 2026-08-19).
 *
 * Why not an icon package: the reference the PO showed (MoMo) is a grid of MULTI-COLOUR
 * icons, and the maintained React icon sets — lucide-react 1.32.0, @phosphor-icons/react
 * 2.1.10, both checked 2026-08-19 — draw single-colour glyphs. Emoji are colourful, there are
 * thousands of them, the phone's own keyboard is the picker for anything not on our shortlist,
 * and they cost the bundle nothing. The trade-off accepted: the artwork belongs to the
 * operating system, so an iPhone and an Android draw the same category slightly differently.
 *
 * Stored in `categories.icon` (a `text` column that has existed since the baseline).
 */

/**
 * The picker, grouped the way a spending app groups things (the PO's reference, MoMo, does
 * the same). Grouping is what makes ~100 choices findable — a flat grid that size is a
 * scavenger hunt.
 *
 * Income has its own group: the "Danh mục thu" tab needs lương / thưởng / kinh doanh / lợi
 * nhuận / thu hồi nợ just as much as the expense tab needs food and fuel.
 */
export const CATEGORY_EMOJI_GROUPS: ReadonlyArray<{
  readonly label: string;
  readonly emojis: readonly string[];
}> = [
  {
    label: 'Ăn uống',
    emojis: ['🍜', '🍚', '🍽️', '☕', '🧋', '🍺', '🍷', '🎂', '🥖', '🍢', '🍿', '🧺'],
  },
  {
    label: 'Đi lại',
    emojis: ['🚗', '🛵', '🚕', '🚌', '🚆', '🚲', '⛽', '🅿️', '🔧', '✈️', '🧳', '🏨'],
  },
  {
    label: 'Nhà cửa & hóa đơn',
    emojis: ['🏠', '🏡', '🔑', '⚡', '💧', '🔥', '📶', '📺', '📱', '🧾', '🪑', '🧹'],
  },
  {
    label: 'Mua sắm & làm đẹp',
    emojis: ['🛒', '🛍️', '👕', '👗', '👟', '👜', '💄', '💅', '💇', '💆', '⌚', '💻'],
  },
  {
    label: 'Sức khỏe & thể thao',
    emojis: ['🏥', '💊', '🩺', '🦷', '🛡️', '🏋️', '⚽', '🏀', '🧘', '🚬', '🐶', '🐱'],
  },
  {
    label: 'Học hành & con cái',
    emojis: ['🎓', '📚', '🏫', '✏️', '🎒', '👶', '🍼', '🧸', '🚼', '🎨'],
  },
  {
    label: 'Giải trí',
    emojis: ['🎬', '🎮', '🎵', '🎧', '🎤', '📷', '🎟️', '🎉', '🏖️', '📦'],
  },
  {
    label: 'Gia đình & lễ nghĩa',
    emojis: ['👪', '💍', '💒', '🎁', '🧧', '❤️', '🤝', '🙏', '🕯️', '🌸'],
  },
  {
    label: 'Thu nhập & tài chính',
    emojis: ['💼', '💵', '💰', '🏅', '💹', '📈', '🏦', '🏧', '💳', '🪙', '🏢', '🏪', '🧮', '💸'],
  },
];

/** Every emoji the picker offers, flat — in the order the groups list them. */
export const CATEGORY_EMOJI_CHOICES: readonly string[] = CATEGORY_EMOJI_GROUPS.flatMap(
  (group) => group.emojis,
);

/** What a category with no icon of its own gets, guessed from its name. */
const NAME_HINTS: ReadonlyArray<readonly [RegExp, string]> = [
  // ORDER MATTERS — the first match wins, so the specific patterns come first.
  // Income first: "Lương" must not fall through to the generic money emoji, and "Lãi" must
  // not be read as anything else.
  [/lương|trợ cấp|phụ cấp/i, '💼'],
  [/thưởng|hoa hồng/i, '🏅'],
  [/kinh doanh|bán hàng|cửa hàng/i, '🏪'],
  [/lợi nhuận|lãi|cổ tức|đầu tư/i, '📈'],
  [/hoàn thuế|thuế/i, '🧾'],
  [/thu hồi nợ|cho vay|đòi nợ|trả nợ|vay/i, '🤝'],
  [/tiết kiệm|dự phòng/i, '💰'],
  [/rút tiền|atm/i, '🏧'],
  [/thẻ tín dụng|thẻ/i, '💳'],
  [/cà phê|cafe|trà sữa/i, '☕'],
  [/điện thoại|di động|sim|cước/i, '📱'],
  [/internet|wifi|mạng|truyền hình|cáp quang/i, '📶'],
  [/nước(?!\s*hoa)|thuỷ|thủy/i, '💧'],
  [/điện(?!\s*thoại)/i, '⚡'],
  [/bảo hiểm/i, '🛡️'],
  [/hóa đơn|hoá đơn|tiện ích/i, '🧾'],
  [/ăn|uống|chợ|thực phẩm|nhà hàng/i, '🍜'],
  // Before the transport rule on purpose: "Sửa xe" is a repair, not a ride.
  [/sửa chữa|bảo trì|sửa xe|sửa nhà/i, '🔧'],
  [/đi lại|xe|xăng|taxi|di chuyển|grab|gửi xe/i, '🚗'],
  [/nhà|thuê|nội thất/i, '🏠'],
  [/mua sắm|quần áo|đồ dùng|siêu thị/i, '🛒'],
  [/sức kh|thuốc|bệnh|răng|khám/i, '🏥'],
  [/giải trí|game|phim|nhạc|thể thao/i, '🎬'],
  [/du lịch|nghỉ|tết/i, '✈️'],
  [/học|giáo dục|sách|khóa/i, '🎓'],
  [/quà|hiếu|sinh nhật|cưới|mừng/i, '🎁'],
  // Before the charity rule: "Hỗ trợ gia đình" is money to relatives, not a donation.
  [/hỗ trợ gia đình|biếu|phụng dưỡng/i, '👪'],
  [/từ thiện|ủng hộ|hỗ trợ/i, '❤️'],
  [/thờ|cúng|lễ chùa/i, '🕯️'],
  [/cưới|hỏi|kết hôn/i, '💒'],
  [/con|em bé|trẻ|sữa/i, '👶'],
  [/thú cưng|chó|mèo/i, '🐶'],
  [/làm đẹp|tóc|spa|mỹ phẩm/i, '💄'],
  [/gym|thể hình|yoga/i, '🏋️'],
  [/người thân|gia đình|bố mẹ|ba mẹ/i, '👪'],
];

/**
 * Categories created during the short life of the hand-drawn icon set store a key like
 * "food" instead of an emoji. They keep working: the key is translated here rather than left
 * to render as literal text in the grid.
 */
const LEGACY_KEYS: Record<string, string> = {
  food: '🍜',
  transport: '🚗',
  home: '🏠',
  shopping: '🛒',
  power: '⚡',
  water: '💧',
  internet: '📶',
  phone: '📱',
  bill: '🧾',
  health: '🏥',
  insurance: '🛡️',
  fun: '🎬',
  travel: '✈️',
  education: '🎓',
  gift: '🎁',
  charity: '❤️',
  ritual: '🕯️',
  saving: '💰',
  other: '💸',
};

export const DEFAULT_CATEGORY_EMOJI = '💸';

/**
 * Reduces what the user typed to a single emoji, or null when it is not one.
 *
 * A grapheme, not a code point: 👨‍👩‍👧 and ✈️ are several code points each, and slicing them
 * would leave a broken half on screen. Letters and digits are refused — the field is for an
 * icon, and "Cắt tóc" as an icon is not what anyone meant.
 */
export function normalizeCategoryEmoji(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const first = firstGrapheme(trimmed);
  if (first === '') return null;
  if (/[\p{L}\p{N}\s]/u.test(first)) return null;

  return first;
}

function firstGrapheme(value: string): string {
  // Intl.Segmenter is the correct tool; Array.from keeps a very old browser from crashing,
  // at the cost of splitting a multi-code-point emoji there.
  if (typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter('vi', { granularity: 'grapheme' });
    return [...segmenter.segment(value)][0]?.segment ?? '';
  }
  return Array.from(value)[0] ?? '';
}

/** The emoji to draw for a category: its own, else one guessed from the name, else the default. */
export function resolveCategoryEmoji(category: { icon?: string | null; name: string }): string {
  const stored = category.icon?.trim();
  if (stored) {
    if (LEGACY_KEYS[stored]) return LEGACY_KEYS[stored];
    const normalized = normalizeCategoryEmoji(stored);
    if (normalized) return normalized;
  }

  for (const [pattern, emoji] of NAME_HINTS) {
    if (pattern.test(category.name)) return emoji;
  }
  return DEFAULT_CATEGORY_EMOJI;
}
